import { Reducer } from "redux";

import {
  BaseReduxPubsubSlice,
  SliceOptionsType,
  getPublishableAction,
} from "./base_redux_pubsub_slice";

import { EnhancedReducerActionType } from "shared/types";

// ReduxPubsubSlice is used for slices that not need to be acknowledged by the server.
// For example, the monitoring slice is a ReduxPubsubSlice because it is only used for
// monitoring and does not need to be synced across clients.
export class ReduxPubsubSlice<StateType> extends BaseReduxPubsubSlice<StateType> {
  reducer: Reducer<StateType, EnhancedReducerActionType>;

  constructor(options: SliceOptionsType<StateType>) {
    super(options);
    this.reducer = this.getReducer();
  }

  // Enhance the slice reducer, which will be called whenever an action is dispatched.
  // - For publishable slices, the reducer will also publish the action to the server.
  // - For syncable slices, the reducer will also reset the local state when receiving a sync message.
  getReducer(): Reducer<StateType, EnhancedReducerActionType> {
    if (!this.channel || !this.flightAuthToken) {
      return this._reducer;
    }

    this.setUpSocket();

    // Call the reducer with empty action to populate the initial state
    const initialState = this.reduce(undefined, { type: "INIT" });

    // eslint-disable-next-line default-param-last
    return (state = initialState, action: EnhancedReducerActionType) => {
      if (
        action.meta &&
        "socketType" in action.meta &&
        action.meta.socketType &&
        action.meta.socketType !== this.socket?.socketType
      ) {
        // Dispatched action from the socket is not meant for this slice
        return state;
      }
      if (action.type === "request-sync") {
        if (this.requestsSync) {
          const { type, id } = action;
          this.socketSend({ type, id });
        }
        return state;
      }
      if (action.type === "sync") {
        if (!this.requestsSync) {
          return state;
        }
        return action.state as StateType;
      }
      if (action.type === "ack") {
        window.flightLogger?.warn("Received ACK for non-ACK slice", action);
        return state;
      }
      const newState = this.reduce(state, action);
      // If the action does not produce any state change, there is no need to
      // publish.
      if (newState === state) {
        return state;
      }
      if (this.publishActions) {
        this.socketSend({
          type: "action",
          action: getPublishableAction(action, this.userId),
        });
      }

      return newState;
    };
  }
}
